// src/slices/permission/thunk.ts
// Nạp toàn bộ quyền (route/menu + button/input) của user hiện tại vào Redux global state — xem mục
// 9, README_SERCURITY.md. Gọi 1 lần lúc login (slices/auth/login/thunk.ts), lúc F5/mở lại trình
// duyệt với token còn hạn (Routes/AuthProtected.tsx, khi state.Permission.loaded === false), và khi
// gặp 403 ở bất kỳ API nào khác trong app (nghĩa là quyền vừa bị đổi — AuthProtected lắng nghe qua
// helpers/api_helper.ts#onForbidden rồi dispatch lại thunk này).
//
// Dữ liệu lấy từ 2 API đã có sẵn, được BE cache Redis theo roleId (xem mục 9.5, README_SERCURITY.md
// — RoleAllowedPathsCacheService/RolePageActionCodesCacheService, đã xác nhận cache-aside đúng
// chuẩn) nên gọi lại vẫn đủ nhanh.

import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  getMyAllowedPaths,
  getMyPageActionInputCodes,
  getUserMenu,
} from "helpers/fakebackend_helper";
import { PageActionPermission } from "services/pageActionPermission/PageActionPermission";
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('permission_messages');

export interface PermissionData {
  isFullAccessPaths: boolean;
  allowedPaths: string[];
  isFullAccessActions: boolean;
  allowedActionCodes: string[];
  // Url của trang ĐẦU TIÊN (theo đúng thứ tự hiển thị trong menu — BE đã OrderBy DisplayOrder/Name,
  // xem GetUserMenuQueryHandler) mà role hiện tại được phép xem — dùng để thay thế việc trỏ cứng
  // "/dashboard" (nhiều role không có quyền Dashboard, gây 403 ngay khi vào app). null nếu role
  // không có quyền xem bất kỳ trang nào, hoặc nếu tải menu thất bại (best-effort, không chặn luồng
  // chính — route-guard/action-guard vẫn hoạt động bình thường dù thiếu url này).
  firstAllowedPath: string | null;
}

// Duyệt cây menu (shape giống MenuNode trong Layouts/useDynamicMenu.ts: { url, children }) theo
// thứ tự DFS, trả về url của node lá (không có children) đầu tiên có url — chính là trang đầu tiên
// người dùng nhìn thấy trong sidebar.
const findFirstMenuUrl = (nodes: any[]): string | null => {
  for (const node of nodes || []) {
    const children = node?.children || [];
    if (children.length > 0) {
      const found = findFirstMenuUrl(children);
      if (found) return found;
    } else if (node?.url) {
      return node.url;
    }
  }
  return null;
};

export const fetchMyPermissions = createAsyncThunk<
  PermissionData,
  void,
  { rejectValue: { message: string; sessionExpired: boolean } }
>(
  "Permission/fetchMyPermissions",
  async (_arg, { rejectWithValue }) => {
    try {
      const [pathsResponse, actionsResponse]: [any, any] = await Promise.all([
        getMyAllowedPaths(),
        getMyPageActionInputCodes(),
      ]);

      const pathsData = pathsResponse?.data ||
        pathsResponse || { isFullAccess: false, paths: [] };
      const actionsData = actionsResponse?.data ||
        actionsResponse || { isFullAccess: false, codes: [] };

      const result: PermissionData = {
        isFullAccessPaths: !!pathsData.isFullAccess,
        allowedPaths: pathsData.paths || [],
        isFullAccessActions: !!actionsData.isFullAccess,
        allowedActionCodes: actionsData.codes || [],
        firstAllowedPath: null,
      };

      // Đồng bộ PageActionPermission (static class, dùng bởi 60 file trong pages/**) ngay khi có kết
      // quả mới nhất — giữ nguyên toàn bộ call site canDisplay/canEnable/canDisplayAny hiện có, chỉ
      // đổi nguồn nạp dữ liệu từ "tự gọi API riêng lúc login" sang "ăn theo thunk Redux này".
      PageActionPermission.setAllowedCodes({
        isFullAccess: result.isFullAccessActions,
        codes: result.allowedActionCodes,
      });

      // Tải menu để tính firstAllowedPath — cố ý tách riêng, KHÔNG gộp vào Promise.all ở trên: đây
      // chỉ là dữ liệu bổ trợ cho việc chọn trang mặc định, lỗi ở bước này không được phép làm hỏng
      // route-guard/action-guard (2 API cốt lõi) đã tải thành công ở trên.
      try {
        const menuResponse: any = await getUserMenu();
        const menuTree = menuResponse?.data || menuResponse || [];
        result.firstAllowedPath = findFirstMenuUrl(menuTree);
      } catch (menuError) {
        console.error("Không tải được menu để xác định trang mặc định:", menuError);
      }

      return result;
    } catch (error: any) {
      console.error("Không tải được thông tin phân quyền:", error);
      return rejectWithValue({
        message: error?.message || st('khong_tai_duoc_thong_tin_phan_text'),
        sessionExpired: error?.status === 401,
      });
    }
  },
  {
    // Chống dồn request: nhiều nơi có thể dispatch thunk này gần như cùng lúc hoặc rải rác liên tiếp
    // (ví dụ nhiều API khác trên cùng 1 trang đều trả 403 → mỗi cái đều gọi lại qua onForbidden
    // trong AuthProtected.tsx). condition đọc thẳng state Redux hiện tại (không bị stale closure như
    // check trong component) — bỏ qua dispatch nếu đang có 1 lần fetch chạy dở (loading), hoặc lần
    // gọi gần nhất chưa quá 5 giây (tránh dồn dập khi 403 đến rải rác không đồng thời).
    condition: (_arg, { getState }) => {
      const state: any = getState();
      const permission = state.Permission;
      if (!permission) return true;
      if (permission.loading) return false;
      if (permission.lastAttemptAt && Date.now() - permission.lastAttemptAt < 5000) {
        return false;
      }
      return true;
    },
  },
);
